import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Mic, Monitor, Pencil, Plus, Sparkles, Square, X } from 'lucide-react';
import { LiveCapture, liveCaptureSupported } from '../Meetings/liveCapture';

// Micro (+ onglet en visio) -> /circle-meetings/{id}/live-audio, réservé au secrétaire ; `path` : un autre direct
// (kickoff de lancement : /kickoffs/{id}/live-audio).
export const useLiveAudio = ({ apiUrl, meetingId, getAuthHeaders, path }) => {
  const [status, setStatus] = useState('idle'); // idle | starting | live | stopping
  const [error, setError] = useState(null);
  const [level, setLevel] = useState(0);
  const socketRef = useRef(null);
  const captureRef = useRef(null);

  const release = useCallback(() => {
    captureRef.current?.stop();
    captureRef.current = null;
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket && socket.readyState <= WebSocket.OPEN) {
      try {
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'stop' }));
        socket.close();
      } catch (err) {
        // déjà fermé
      }
    }
    setLevel(0);
  }, []);

  const start = useCallback(async (source, projects = []) => {
    if (!meetingId || socketRef.current) return;
    setError(null);
    setStatus('starting');
    // La capture d'abord : le partage d'onglet exige le clic de l'utilisateur, sans attente réseau avant.
    const capture = new LiveCapture({
      source,
      onLevel: setLevel,
      onPcm: (buffer) => {
        const socket = socketRef.current;
        if (socket?.readyState === WebSocket.OPEN && socket.started) socket.send(buffer);
      },
      onTabAudioEnded: () => {
        setError("Le partage de l'onglet s'est arrêté.");
        release();
        setStatus('idle');
      },
    });
    try {
      await capture.start();
    } catch (err) {
      setError(err?.message || "Impossible d'ouvrir le micro.");
      setStatus('idle');
      return;
    }
    captureRef.current = capture;
    const token = (getAuthHeaders()?.Authorization || '').replace(/Bearer\s+/i, '');
    const wsBase = (apiUrl || '').replace(/^http/i, 'ws');
    const socket = new WebSocket(`${wsBase}${path || `/circle-meetings/${meetingId}/live-audio`}?token=${encodeURIComponent(token)}`);
    socket.binaryType = 'arraybuffer';
    socketRef.current = socket;
    socket.onopen = () => socket.send(JSON.stringify({ type: 'start', language: 'fr', projects }));
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'started') {
          socket.started = true;
          setStatus('live');
        } else if (data.type === 'error') {
          setError(data.message || 'Démarrage impossible.');
        }
      } catch (err) {
        // message binaire ou illisible : ignoré
      }
    };
    socket.onclose = (event) => {
      if (socketRef.current !== socket) return; // arrêt demandé
      if (event.reason) setError(event.reason);
      else if (!socket.started) setError('Connexion au direct impossible.');
      release();
      setStatus('idle');
    };
  }, [apiUrl, meetingId, getAuthHeaders, path, release]);

  const stop = useCallback(() => {
    setStatus('stopping');
    release();
    setStatus('idle');
  }, [release]);

  useEffect(() => release, [release]);

  return { status, error, level, start, stop };
};

export const LevelMeter = ({ level }) => (
  <div className="flex items-end gap-0.5 h-4" aria-hidden>
    {[0.15, 0.35, 0.55, 0.75].map((threshold) => (
      <span
        key={threshold}
        className={`w-1 rounded-sm transition-colors ${level > threshold ? 'bg-emerald-500' : 'bg-slate-200'}`}
        style={{ height: `${25 + threshold * 100}%` }}
      />
    ))}
  </div>
);

export const LiveSecretaryPanel = ({
  apiUrl,
  meetingId,
  getAuthHeaders,
  live,
  userIsSecretary,
  projects = [],
}) => {
  const audio = useLiveAudio({ apiUrl, meetingId, getAuthHeaders });
  const [source, setSource] = useState('salle');
  const active = Boolean(live?.active);

  // Réunion quittée par le secrétaire (autre onglet, transfert) : le direct côté serveur s'arrête avec la connexion.
  const capturing = audio.status === 'live' || audio.status === 'starting';
  if (!active && !userIsSecretary) return null;

  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`relative flex h-2.5 w-2.5 ${active ? '' : 'opacity-40'}`}>
            {active && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${active ? 'bg-rose-500' : 'bg-slate-400'}`} />
          </span>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Secrétaire en direct</p>
          {active && (
            <span className="text-xs text-slate-400">Nexus écoute et note les points clés</span>
          )}
        </div>
        {userIsSecretary && (
          <div className="flex items-center gap-2">
            {capturing && <LevelMeter level={audio.level} />}
            {!capturing && !active && (
              <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs font-semibold">
                {[['salle', 'En salle', Mic], ['visio', 'Visio', Monitor]].map(([key, label, Icon]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSource(key)}
                    className={`flex items-center gap-1 rounded-md px-2 py-1 ${source === key ? 'bg-slate-800 text-white' : 'text-slate-500'}`}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            )}
            {active && !capturing ? (
              <span className="text-xs text-slate-400">Son capté depuis un autre onglet</span>
            ) : capturing ? (
              <button
                type="button"
                onClick={audio.stop}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-100 rounded-lg hover:bg-rose-100"
              >
                <Square size={12} /> Arrêter
              </button>
            ) : (
              <button
                type="button"
                onClick={() => audio.start(source, projects)}
                disabled={!live?.mac_connected || !liveCaptureSupported()}
                title={!live?.mac_connected ? "Le Mac d'Innowide n'est pas connecté" : undefined}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {audio.status === 'starting' ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} Démarrer
              </button>
            )}
          </div>
        )}
      </div>

      {userIsSecretary && !active && !capturing && (
        <p className="text-xs text-slate-400">
          {live?.mac_connected
            ? "Nexus écoute sur le Mac d'Innowide (rien ne sort de chez nous). En revue des projets, il note les points clés de chaque projet : ils se complètent quelques secondes après le passage au suivant, dans le rappel en haut. À l'ordre du jour, il écrit la synthèse de chaque point traité. Après la réunion, il relit tout et propose à chacun, sur sa page d'accueil, les engagements pris. Tu peux tout corriger."
            : "Le Mac d'Innowide n'est pas connecté : le direct est indisponible pour le moment."}
        </p>
      )}
      {(audio.error || live?.warning) && (
        <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{audio.error || live.warning}</p>
      )}
    </div>
  );
};

const SAVE_TIMEOUT_MS = 12000;

// Liste de puces écrite par Nexus, enregistrée et modifiable par le secrétaire (Nexus n'y touche plus ensuite).
// Les puces nouvelles ou réécrites par Nexus glissent en place et restent surlignées un instant.
// `variant="recap"` : rappel plus discret d'un projet déjà passé (`heading` = son nom, `pendingLabel` tant que
// Nexus n'a pas fini ses puces, `extra` = bouton en plus dans l'en-tête).
export const MuseBulletsCard = ({
  title, bullets, source, listening, updating, userIsSecretary, onSave, emptyListening, emptyIdle, lockHint, lastError,
  heading, pendingLabel, variant = 'card', extra,
}) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const savingRef = useRef(false);
  const sentAtRef = useRef(0);
  const previous = useRef(null);
  const [fresh, setFresh] = useState(() => new Set());

  useEffect(() => {
    const before = previous.current;
    previous.current = bullets;
    if (!before) return undefined;
    const known = new Set(before);
    const changed = new Set(bullets.filter((bullet) => !known.has(bullet)));
    setFresh(changed);
    if (!changed.size) return undefined;
    const timer = setTimeout(() => setFresh(new Set()), 2500);
    return () => clearTimeout(timer);
  }, [bullets]);
  // Les puces enregistrées reviennent du serveur : le brouillon se referme.
  useEffect(() => {
    if (!savingRef.current) return;
    savingRef.current = false;
    setSaving(false);
    setDraft(null);
  }, [bullets, source]);

  useEffect(() => {
    if (!savingRef.current || !lastError || lastError.at < sentAtRef.current) return;
    savingRef.current = false;
    setSaving(false);
    setSaveError(lastError.message);
  }, [lastError]);

  useEffect(() => {
    if (!saving) return undefined;
    const timer = setTimeout(() => {
      if (!savingRef.current) return;
      savingRef.current = false;
      setSaving(false);
      setSaveError("Pas de confirmation du serveur : réessaie.");
    }, SAVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [saving]);

  const bySecretary = source === 'secretaire';
  const setBullet = (index, value) => setDraft((prev) => prev.map((item, i) => (i === index ? value : item)));
  const save = () => {
    const cleaned = draft.map((item) => item.trim()).filter(Boolean);
    setSaveError(null);
    if (onSave?.(cleaned)) {
      savingRef.current = true;
      sentAtRef.current = Date.now();
      setSaving(true);
    } else {
      setSaveError("Enregistrement impossible (connexion temps réel).");
    }
  };
  const input = 'flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100';
  const recap = variant === 'recap';
  const working = pendingLabel || (listening && updating ? 'Nexus met à jour…' : null);

  return (
    <div className={recap
      ? 'rounded-xl border border-indigo-100 bg-gradient-to-b from-indigo-50/80 to-white p-4 space-y-2'
      : 'bg-white p-5 rounded-xl border border-indigo-100 shadow-sm space-y-3'}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 uppercase tracking-wider">
            <Sparkles size={12} /> {title}
          </p>
          {heading && <p className="mt-0.5 truncate text-base font-semibold text-slate-800">{heading}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[11px] text-slate-400">
          {working && !draft && (
            <span className="flex items-center gap-1 text-indigo-500"><Loader2 size={12} className="animate-spin" /> {working}</span>
          )}
          {!draft && !working && bullets.length > 0 && <span>{bySecretary ? 'Validé par le secrétaire' : 'Rédigé par Nexus'}</span>}
          {!draft && extra}
          {userIsSecretary && onSave && !draft && (
            <button
              type="button"
              onClick={() => setDraft(bullets.length ? [...bullets] : [''])}
              disabled={saving}
              className="flex items-center gap-1 px-2.5 py-1 font-semibold text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-60"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} />} {bullets.length ? 'Modifier' : 'Écrire'}
            </button>
          )}
        </div>
      </div>

      {draft ? (
        <div className="space-y-2">
          {draft.map((bullet, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <input
                className={input}
                value={bullet}
                autoFocus={index === draft.length - 1 && !bullet}
                onChange={(event) => setBullet(index, event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') { event.preventDefault(); setDraft((prev) => [...prev, '']); }
                }}
              />
              <button type="button" onClick={() => setDraft((prev) => prev.filter((_, i) => i !== index))} className="p-1 text-slate-300 hover:text-rose-500" title="Supprimer">
                <X size={14} />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={() => setDraft((prev) => [...prev, ''])} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700">
              <Plus size={12} /> Ajouter une puce
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setSaveError(null);
                  setDraft(null);
                }}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-semibold text-slate-500 rounded-lg hover:bg-slate-100 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Enregistrer
              </button>
            </div>
          </div>
          {saveError && <p className="text-xs text-rose-600">{saveError}</p>}
          {listening && <p className="text-[11px] text-slate-400">{lockHint}</p>}
        </div>
      ) : bullets.length === 0 ? (
        <p className="text-sm text-slate-400 italic">
          {listening || pendingLabel ? (updating ? 'Synthèse en cours…' : emptyListening) : emptyIdle}
        </p>
      ) : (
        <ul className="space-y-1">
          {bullets.map((bullet) => (
            <li
              key={bullet}
              className={`flex gap-2 rounded-md px-1.5 py-0.5 text-sm text-slate-700 transition-colors duration-1000 ${fresh.has(bullet) ? 'bg-indigo-50 animate-muse-line-in' : 'bg-transparent'}`}
            >
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const findNote = (notes, key, id) => (notes || []).find((entry) => String(entry[key]) === String(id)) || null;

// « Quoi de neuf » du projet sélectionné : enregistré dans la revue du projet de cette réunion.
export const ProjectNewsCard = ({ live, projectId, review, userIsSecretary, onSave }) => {
  const note = useMemo(() => findNote(live?.project_notes, 'project_id', projectId), [live?.project_notes, projectId]);
  const saved = review?.news;
  const bullets = useMemo(
    () => (Array.isArray(saved) && saved.length ? saved : (note?.bullets || [])),
    [saved, note?.bullets]
  );
  const listening = Boolean(live?.active) && review?.news_source !== 'secretaire' && !note?.locked;
  if (!projectId || (!bullets.length && !listening && !userIsSecretary)) return null;
  return (
    <MuseBulletsCard
      key={projectId}
      title="Quoi de neuf"
      bullets={bullets}
      source={review?.news_source}
      listening={listening}
      updating={note?.updating}
      userIsSecretary={userIsSecretary}
      onSave={onSave ? (items) => onSave(projectId, items) : null}
      emptyListening="Les points clés apparaîtront ici pendant que le responsable du projet parle."
      emptyIdle="Aucun quoi de neuf pour ce projet."
      lockHint="Une fois enregistré, Nexus ne modifiera plus ce quoi de neuf."
      lastError={live?.lastError}
    />
  );
};

// Synthèse du point de l'ordre du jour sélectionné : Nexus l'écrit quand l'équipe passe au point suivant.
export const PointSummaryCard = ({ live, point, userIsSecretary, onSave }) => {
  const note = useMemo(() => findNote(live?.point_notes, 'point_id', point?.id), [live?.point_notes, point?.id]);
  const summary = point?.summary;
  const bullets = useMemo(() => (Array.isArray(summary) ? summary : []), [summary]);
  const listening = Boolean(live?.active) && point?.summary_source !== 'secretaire';
  if (!point || (!bullets.length && !listening && !userIsSecretary)) return null;
  return (
    <MuseBulletsCard
      key={point.id}
      title="Synthèse du point"
      bullets={bullets}
      source={point.summary_source}
      listening={listening}
      updating={note?.updating}
      userIsSecretary={userIsSecretary}
      onSave={onSave ? (items) => onSave(point.id, items) : null}
      emptyListening="Nexus rédigera la synthèse au passage au point suivant."
      emptyIdle="Pas encore de synthèse pour ce point."
      lockHint="Une fois enregistrée, Nexus ne modifiera plus cette synthèse."
      lastError={live?.lastError}
    />
  );
};
